<template>
    <Dialog :open="open" @update:open="handleClose">
        <DialogContent class="max-h-[85vh] overflow-y-auto sm:max-w-[425px]">
            <DialogHeader>
                <DialogTitle>
                    <span v-if="!currentSettings">Set up your own </span>
                    <span v-else>Edit </span>

                    <span v-if="provider === 'gcs'">Google Cloud bucket</span>
                    <span v-else-if="provider === 'aws'">Amazon S3 bucket</span>
                    <span v-else-if="provider === 'digitalocean'">DigitalOcean Spaces bucket</span>
                    <span v-else>Cloudflare R2 bucket</span>
                </DialogTitle>
            </DialogHeader>

            <div class="space-y-6">
                <!-- Bucket name -->
                <div>
                    <Label for="bucket-name">Bucket name*</Label>
                    <Input
                        id="bucket-name"
                        v-model="v$.form.settings.name.$model"
                        placeholder="Enter your bucket name"
                        :aria-invalid="!!getError('name')"
                        class="mt-2"
                    />
                    <p
                        v-if="getError('name')"
                        class="mt-1.5 text-xs text-destructive"
                    >
                        {{ getError('name') }}
                    </p>
                </div>

                <!-- Region -->
                <div
                    v-if="
                        provider === 'aws' ||
                        provider === 'gcs' ||
                        provider === 'digitalocean'
                    "
                >
                    <Label for="region">Region*</Label>
                    <Input
                        id="region"
                        v-model="v$.form.settings.region.$model"
                        :placeholder="
                            provider === 'digitalocean'
                                ? 'nyc3'
                                : 'europe-west3'
                        "
                        :aria-invalid="!!getError('region')"
                        class="mt-2"
                    />
                    <p
                        v-if="getError('region')"
                        class="mt-1.5 text-xs text-destructive"
                    >
                        {{ getError('region') }}
                    </p>
                </div>

                <!-- Client Email (GCS) -->
                <div v-if="provider === 'gcs'">
                    <Label for="client-email">Client Email*</Label>
                    <Input
                        id="client-email"
                        v-model="state.form.settings.email"
                        placeholder="myowntopolioplugin@example-io.iam.gserviceaccount.com"
                        :aria-invalid="!!getError('email')"
                        class="mt-2"
                        @blur="v$.form.settings.email.$touch()"
                    />
                    <p
                        v-if="getError('email')"
                        class="mt-1.5 text-xs text-destructive"
                    >
                        {{ getError('email') }}
                    </p>
                </div>

                <!-- Account ID (Cloudflare) -->
                <div v-if="provider === 'cloudflare'">
                    <Label for="account-id">Account ID*</Label>
                    <Input
                        id="account-id"
                        v-model="v$.form.settings.account_id.$model"
                        placeholder="Account ID"
                        :aria-invalid="!!getError('account_id')"
                        class="mt-2"
                    />
                    <p
                        v-if="getError('account_id')"
                        class="mt-1.5 text-xs text-destructive"
                    >
                        {{ getError('account_id') }}
                    </p>
                </div>

                <!-- Project ID (GCS) -->
                <div v-if="provider === 'gcs'">
                    <Label for="project-id">Project ID*</Label>
                    <Input
                        id="project-id"
                        v-model="v$.form.settings.project_id.$model"
                        placeholder="My-own-google-project-id"
                        :aria-invalid="!!getError('projectID')"
                        class="mt-2"
                    />
                    <p
                        v-if="getError('projectID')"
                        class="mt-1.5 text-xs text-destructive"
                    >
                        {{ getError('projectID') }}
                    </p>
                </div>

                <!-- Access Key (AWS/Cloudflare/DigitalOcean) -->
                <div
                    v-if="
                        provider === 'aws' ||
                        provider === 'cloudflare' ||
                        provider === 'digitalocean'
                    "
                >
                    <Label for="access-key">Access key*</Label>
                    <Input
                        id="access-key"
                        v-model="v$.form.settings.access_key.$model"
                        placeholder="Enter your access key"
                        :aria-invalid="!!getError('access_key')"
                        class="mt-2"
                    />
                    <p
                        v-if="getError('access_key')"
                        class="mt-1.5 text-xs text-destructive"
                    >
                        {{ getError('access_key') }}
                    </p>
                </div>

                <!-- Private/Secret Key -->
                <div>
                    <Label for="private-key">
                        {{
                            provider === 'cloudflare' ||
                            provider === 'digitalocean'
                                ? 'Secret key*'
                                : 'Private key*'
                        }}
                    </Label>
                    <Textarea
                        id="private-key"
                        :model-value="state.form.settings.private_key"
                        :placeholder="
                            provider === 'cloudflare' ||
                            provider === 'digitalocean'
                                ? 'Enter your secret key'
                                : 'Enter your private key'
                        "
                        :aria-invalid="!!getError('private_key')"
                        class="mt-2"
                        @update:model-value="handleUpdatePrivateKey"
                        @focus="clearMaskedPrivateKey"
                        @blur="v$.form.settings.private_key.$touch()"
                    />
                    <p
                        v-if="getError('private_key')"
                        class="mt-1.5 text-xs text-destructive"
                    >
                        {{ getError('private_key') }}
                    </p>
                </div>

                <!-- URL -->
                <div
                    v-if="
                        provider === 'cloudflare' ||
                        provider === 'aws' ||
                        provider === 'digitalocean'
                    "
                >
                    <Label for="url">
                        {{
                            provider === 'aws'
                                ? 'CloudFront URL'
                                : provider === 'digitalocean'
                                  ? 'Public URL'
                                  : 'Public URL*'
                        }}
                    </Label>
                    <Input
                        id="url"
                        v-model="v$.form.settings.url.$model"
                        :placeholder="
                            provider === 'aws'
                                ? 'https://d111111abcdef8.cloudfront.net'
                                : provider === 'digitalocean'
                                  ? 'https://example.nyc3.digitaloceanspaces.com'
                                  : 'https://example.cloudflare.com'
                        "
                        :aria-invalid="!!getError('url')"
                        class="mt-2"
                    />
                    <p v-if="getError('url')" class="mt-1.5 text-xs text-destructive">
                        {{ getError('url') }}
                    </p>
                </div>

                <!-- Jurisdiction (Cloudflare) -->
                <div v-if="provider === 'cloudflare'">
                    <Label for="jurisdiction">Jurisdiction</Label>
                    <Input
                        id="jurisdiction"
                        v-model="v$.form.settings.jurisdiction.$model"
                        placeholder="eu"
                        :aria-invalid="!!getError('jurisdiction')"
                        class="mt-2"
                    />
                    <p
                        v-if="getError('jurisdiction')"
                        class="mt-1.5 text-xs text-destructive"
                    >
                        {{ getError('jurisdiction') }}
                    </p>
                </div>

                <!-- Documentation link -->
                <div>
                    <p class="text-xs leading-normal text-muted-foreground">
                        For more information how to setup
                        <span v-if="provider === 'gcs'">Google Cloud</span>
                        <span v-else-if="provider === 'aws'">Amazon S3</span>
                        <span v-else-if="provider === 'digitalocean'">DigitalOcean Spaces</span>
                        <span v-else>Cloudflare R2</span>
                        bucket please
                        <a
                            :href="
                                allProviders.find((p) => p.key === provider)
                                    ?.docUrl
                            "
                            target="_blank"
                            class="font-semibold underline text-new-blue-500 hover:text-new-blue-600"
                        >
                            check out our documentation</a>.
                    </p>
                </div>

                <!-- Detach section -->
                <div
                    v-if="currentSettings?.storage_provider"
                    class="border-t border-new-neutral-200 pt-4"
                >
                    <div class="text-xs font-medium text-foreground">
                        Detach
                        <span>
                            {{
                                allProviders.find((p) => p.key === provider)
                                    ?.name
                            }}
                        </span>
                        bucket
                    </div>
                    <div class="mt-[7px] text-xs text-muted-foreground">
                        After detaching
                        <span>
                            {{
                                allProviders.find((p) => p.key === provider)
                                    ?.name
                            }}
                        </span>
                        bucket, all of your Plugin users won't be able to access
                        files on your storage. You can reconnect your storage
                        bucket again later.
                    </div>
                    <button
                        class="mt-4 text-xs font-semibold underline text-new-blue-500 hover:text-new-blue-600"
                        @click="$emit('deleteModal')"
                    >
                        Begin detaching
                        <span>
                            {{
                                allProviders.find((p) => p.key === provider)
                                    ?.name
                            }}
                        </span>
                        bucket
                    </button>
                </div>
            </div>

            <DialogFooter>
                <Button variant="ghost" size="lg" @click="handleClose(false)">
                    Cancel
                </Button>

                <Button
                    size="lg"
                    :disabled="
                        loading || v$.$invalid || !hasStorageSettingsEnabled
                    "
                    type="submit"
                    @click="saveStorage"
                >
                    <span v-if="!currentSettings">Finish setup</span>
                    <span v-else>Update custom storage</span>
                </Button>
            </DialogFooter>
        </DialogContent>
    </Dialog>
</template>

<script lang="ts" setup>
import { PropType, ref, reactive, computed, watch } from 'vue';
import { router } from '@inertiajs/vue3';
import { route } from 'ziggy-js';
import useVuelidate from '@vuelidate/core';
import { required, requiredIf, maxLength, email } from '@vuelidate/validators';
import { toast } from 'vue-sonner';
import IToken from '@/types/Plugin/IToken';
import IInputErrors from '@/types/Atomic/IInputErrors';
import ITokenSettings from '@/types/Plugin/ITokenSettings';
import { allProviders } from '@/Pages/PluginSettings/data/storageProviders';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

const props = defineProps({
    open: {
        type: Boolean,
        required: true,
    },
    token: {
        type: Object as PropType<IToken>,
        required: true,
    },
    provider: {
        type: String as PropType<'aws' | 'gcs' | 'cloudflare' | 'digitalocean'>,
        required: true,
    },
    hasStorageSettingsEnabled: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(['close', 'save', 'deleteModal', 'update:open']);

const currentSettings = computed<ITokenSettings | null>(
    () => props.token.settings ?? null
);

const loading = ref(false);
const errors = ref<IInputErrors>({});

const state = reactive({
    form: {
        settings: {
            name: '',
            region: '',
            email: '',
            project_id: '',
            access_key: '',
            private_key: '',
            url: '',
            jurisdiction: '',
            account_id: '',
            aws: false,
            gcs: false,
            cloudflare: false,
            digitalocean: false,
        },
    },
});

const rules = {
    form: {
        settings: {
            name: {
                required,
                maxLength: maxLength(255),
            },
            region: {
                required: requiredIf(
                    () =>
                        props.provider === 'gcs' ||
                        props.provider === 'aws' ||
                        props.provider === 'digitalocean'
                ),
                maxLength: maxLength(255),
            },
            email: {
                required: requiredIf(() => props.provider === 'gcs'),
                maxLength: maxLength(255),
                email,
            },
            project_id: {
                required: requiredIf(() => props.provider === 'gcs'),
                maxLength: maxLength(255),
            },
            access_key: {
                required: requiredIf(
                    () =>
                        props.provider === 'aws' ||
                        props.provider === 'cloudflare' ||
                        props.provider === 'digitalocean'
                ),
                maxLength: maxLength(255),
            },
            private_key: {
                required,
            },
            url: {
                required: requiredIf(() => props.provider === 'cloudflare'),
                maxLength: maxLength(255),
            },
            account_id: {
                required: requiredIf(() => props.provider === 'cloudflare'),
                maxLength: maxLength(255),
            },
            jurisdiction: {
                maxLength: maxLength(255),
            },
        },
    },
};
const v$ = useVuelidate(rules, state);

// The dialog is reused for a different provider without remounting (for example
// after detaching one bucket and setting up another), so the provider flags and
// the prefilled fields have to follow the `provider` prop, not the mount.
watch(
    () => props.provider,
    (provider) => {
        Object.assign(state.form.settings, {
            name: '',
            region: '',
            email: '',
            project_id: '',
            access_key: '',
            private_key: '',
            url: '',
            jurisdiction: '',
            account_id: '',
            aws: provider === 'aws',
            gcs: provider === 'gcs',
            cloudflare: provider === 'cloudflare',
            digitalocean: provider === 'digitalocean',
        });

        const settings = props.token.settings;

        if (settings?.storage_provider === provider) {
            if (provider === 'gcs') {
                state.form.settings.name = settings.storage_gcs_bucket ?? '';
                state.form.settings.region = settings.storage_gcs_region ?? '';
                state.form.settings.project_id =
                    settings.storage_gcs_project_id ?? '';
                state.form.settings.email =
                    settings.storage_gcs_client_email ?? '';
                state.form.settings.private_key =
                    settings.storage_gcs_private_key ?? '';
            } else if (provider === 'aws') {
                state.form.settings.name = settings.storage_aws_bucket ?? '';
                state.form.settings.region = settings.storage_aws_region ?? '';
                state.form.settings.access_key =
                    settings.storage_aws_access_key ?? '';
                state.form.settings.private_key =
                    settings.storage_aws_secret_key ?? '';
                state.form.settings.url =
                    settings.storage_aws_cloudfront_url ?? '';
            } else if (provider === 'cloudflare') {
                state.form.settings.name =
                    settings.storage_cloudflare_bucket ?? '';
                state.form.settings.url =
                    settings.storage_cloudflare_public_url ?? '';
                state.form.settings.account_id =
                    settings.storage_cloudflare_account_id ?? '';
                state.form.settings.access_key =
                    settings.storage_cloudflare_access_key ?? '';
                state.form.settings.private_key =
                    settings.storage_cloudflare_secret_key ?? '';
                state.form.settings.jurisdiction =
                    settings.storage_cloudflare_jurisdiction ?? '';
            } else if (provider === 'digitalocean') {
                state.form.settings.name =
                    settings.storage_digitalocean_bucket ?? '';
                state.form.settings.region =
                    settings.storage_digitalocean_region ?? '';
                state.form.settings.access_key =
                    settings.storage_digitalocean_access_key ?? '';
                state.form.settings.private_key =
                    settings.storage_digitalocean_secret_key ?? '';
                state.form.settings.url =
                    settings.storage_digitalocean_public_url ?? '';
            }
        }

        errors.value = {};
        v$.value.$reset();
    },
    { immediate: true }
);

const handleClose = (value: boolean) => {
    if (!value) {
        emit('close');
        emit('update:open', false);
    }
};

const saveStorage = () => {
    v$.value.$touch();
    if (v$.value.$error) {
        return;
    } else {
        loading.value = true;
        router.put(
            route('api-tokens.storage.update', props.token.id),
            state.form,
            {
                preserveScroll: true,
                onSuccess: () => {
                    emit('close');
                    emit('update:open', false);
                    toast.success('Storage settings updated successfully!');
                },
                onError: (e) => {
                    toast.error(e?.error || 'Failed to update storage settings');
                },
                onFinish: () => {
                    loading.value = false;
                },
            }
        );
    }
};

const handleUpdatePrivateKey = (value: string) => {
    const updatedValue = value.replaceAll('\\n', '\n');
    state.form.settings.private_key = updatedValue;
};

// The secret is prefilled with a masked placeholder (contains •); clear it on
// focus so the user types a fresh secret instead of appending to the mask.
const clearMaskedPrivateKey = () => {
    if (state.form.settings.private_key.includes('•')) {
        state.form.settings.private_key = '';
    }
};

const getError = (field: string) => {
    return (
        v$.value.form.settings[field]?.$errors[0]?.$message ||
        errors.value?.[field] ||
        ''
    );
};
</script>
